import { useCallback, useEffect, useRef, useState } from "react";

import { useDidUpdateEffect } from "../../hooks";
import { DEFAULT_MESSAGE_DELAY } from "./constants";
import { SystemMessageProps } from "./types";

export * from "./types";

enum AnimationType {
  MESSAGE = "message",
  INDICATOR = "indicator",
}

type Animation<T extends AnimationType = AnimationType> = {
  [AnimationType.MESSAGE]: {
    type: AnimationType.MESSAGE;
    message: SystemMessageProps;
  };
  [AnimationType.INDICATOR]: {
    type: AnimationType.INDICATOR;
    messageDelay: number;
  };
}[T];

const createAnimateIndicator = (
  messageDelay: number = DEFAULT_MESSAGE_DELAY,
): Animation<AnimationType.INDICATOR> => ({
  type: AnimationType.INDICATOR,
  messageDelay,
});

/** A hook to animate messages onto the screen */
export const useAnimatedMessages = ({
  messages,
  isLast,
  onEndAnimation,
}: {
  messages: SystemMessageProps[];
  /** Is this messages the most recent in the conversation, user and system messages combined.
   * Animations will only occur for the most recent bot message */
  isLast?: boolean;
  /** Callback to be executed when animation is complete (i.e. all messages are showing)  */
  onEndAnimation?: (messages: SystemMessageProps[]) => any;
}) => {
  const shouldAnimate = useRef(isLast && !!messages.length);
  const [complete, setComplete] = useState(!shouldAnimate.current);
  const [showIndicator, setShowIndicator] = useState(shouldAnimate.current);
  const [visibleMessages, setVisibleMessages] = useState(
    shouldAnimate.current ? [] : messages,
  );

  const endAnimation = useCallback(() => {
    setComplete(true);
    setShowIndicator(false);

    if (isLast && onEndAnimation) {
      // Don't accidentally trigger callback for earlier messages
      onEndAnimation(messages);
    }
  }, [onEndAnimation, messages, isLast]);

  useEffect(() => {
    if (!shouldAnimate) return undefined;

    const animations = messages.flatMap<Animation>((message) => [
      createAnimateIndicator(message.delay),
      { type: AnimationType.MESSAGE, message },
    ]);

    let timer: NodeJS.Timeout;
    const setTimer = (callback: VoidFunction, messageDelay: number) => {
      if (messageDelay === 0) {
        callback();
        return;
      }

      timer = setTimeout(() => {
        callback();
      }, messageDelay);
    };

    const animate = () => {
      if (!shouldAnimate.current) return;

      const next = animations.shift();
      if (!next) {
        endAnimation();
        return;
      }

      if (next.type === AnimationType.MESSAGE) {
        setShowIndicator(false);
        setVisibleMessages((prev) => [...prev, next.message]);
        setTimer(animate, DEFAULT_MESSAGE_DELAY);
      }

      if (next.type === AnimationType.INDICATOR) {
        setShowIndicator(true);
        setTimer(animate, next.messageDelay);
      }
    };

    animate();

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Don't run effect on first render, but do run when `isLast` changes
  useDidUpdateEffect(() => {
    if (!isLast) {
      shouldAnimate.current = false;
      endAnimation();
      setVisibleMessages(messages);
    }
  }, [isLast]);

  return {
    complete,
    showIndicator,
    visibleMessages,
  };
};
